
import Vue from 'vue'
// eslint-disable-next-line import/no-named-as-default
import Hls from 'hls.js'
import { assembleUrl, qsBool } from '~/assets/ts/utils/params'
import { openPopoutWindow } from '~/assets/ts/utils/misc'
import PlayerMuteIcon from '~/components/player/MuteIcon.vue'
import SaIcon from '~/components/_general/SaIcon.vue'
import Poller from '~/components/_general/Poller.vue'
import { CreateHls, IsNativeHls } from '~/assets/ts/utils/hls'

export const VideoPreviewProps = {
  videoStreamUrl: {
    type: String,
    required: true,
  },
  popoutUrl: {
    type: String,
    default: '',
  },
  poster: {
    type: String,
    required: true,
  },
  delay: {
    type: Boolean,
    default: true,
  },
  delayMs: {
    type: Number,
    default: 5,
  },
  loop: {
    type: Boolean,
    default: true,
  },
  controls: {
    type: Boolean,
    default: true,
  },
  volumeOverride: {
    type: Number,
    default: 0,
    validator(value: number) {
      return value >= 0 && value <= 100
    },
  },
  background: {
    type: Boolean,
  },
}

export default Vue.extend({
  name: 'VideoPreview',
  components: { Poller, SaIcon, PlayerMuteIcon },
  props: VideoPreviewProps,
  data() {
    return {
      hoverTime: 0,
      now: 0,
      hls: undefined as Hls | undefined,
      muted: true,
      observer: undefined as IntersectionObserver | undefined,
    }
  },
  computed: {
    volume(): number {
      if (this.volumeOverride > 0) {
        return this.volumeOverride
      }
      return this.$store.getters['player/volume']
    },
    autoplay(): boolean {
      return !this.delay
    },
    debug(): boolean {
      return qsBool(this, 'debug')
    },
    show(): boolean {
      return this.autoplay || this.hoverDuration >= this.delayMs
    },
    hoverDuration(): number {
      if (this.hoverTime === 0) return 0
      return this.now - this.hoverTime
    },
    isHovering(): boolean {
      return this.hoverTime !== 0
    },
    videoMedia(): HTMLVideoElement {
      return this.$refs.video as HTMLVideoElement
    },
    nativeHls(): boolean {
      return IsNativeHls(this, this.videoMedia)
    },
  },
  watch: {
    show() {
      if (this.show) {
        this.setupHls()
      } else {
        this.hls?.destroy()
      }
    },
    volume() {
      this.updateVolume()
    },
  },
  mounted() {
    if (!this.autoplay) return
    this.setupHls()
    if (this.background) this.observeVisibility()
  },
  destroyed() {
    this.hls?.destroy()
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    openPopoutWindow,
    updateVolume() {
      this.videoMedia.volume = this.volume / 100
    },
    disableCaptions() {
      const video = this.videoMedia
      for (let i = 0; i < video.textTracks.length; i++) {
        video.textTracks[i].mode = 'disabled'
      }
    },
    setupHls() {
      if (this.nativeHls) {
        this.videoMedia.src = this.videoStreamUrl
        if (this.background) this.disableCaptions()
        if (this.debug) {
          console.debug('Video Preview using Native Player')
        }
        return
      }
      this.hls = CreateHls(
        assembleUrl(this.videoStreamUrl, { stats: false }),
        this.videoMedia,
        {
          debug: this.debug,
          renderTextTracksNatively: !this.background,
        }
      )
      this.updateVolume()
    },
    observeVisibility() {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            this.hover(entry.isIntersecting)
            if (this.debug) {
              console.debug(
                `Video ${this.videoStreamUrl.slice(-4)} is ${entry.isIntersecting ? '' : 'not '}playing.`
              )
            }
          }
        },
        {
          threshold: 0.1, // Trigger when at least 10% is visible
        }
      )

      this.observer.observe(this.$el)
    },
    updateNow() {
      this.now = new Date().getTime()
    },
    hover(isHovering: boolean) {
      if (!this.delay) return
      this.updateNow()
      this.hoverTime = isHovering ? this.now : 0
    },
  },
})
